import {
  SubSelectionItem,
  SubSelectionStatusId,
  SubSelectionType,
  Event,
} from "@markit/common.types";
import { Fragment, ReactElement, useMemo, useState } from "react";
import { Colors } from "../../../../utils/colors";
import { MassTextsActionItem } from "../Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import {
  isEventExternalLink,
  isEventRelatedType,
  isEventTicketsPaid,
  isLinkedEventType,
  isProductSpecificType,
  isProductWithCheckout,
} from "@markit/common.utils";
import TextCategoriesTipsModal from "../../../DisplayModal/TextCategoriesTipsModal";
import { ActionItemHorizontalDivider } from "../../../Dividers/ActionItemHorizontalDivider";

type SuggestedCategorySection = {
  key: string;
  title: string;
  icon: ReactElement;
  onPress: () => void;
  alternateRightIcon?: ReactElement;
  isCheckSelected?: boolean;
};

type SelectRecipientsSuggestedCategoriesProps = {
  event: Event;
  excludingMode: boolean;
  selectedCategories: SubSelectionItem[];
  selectOnPress: (
    tabValue: number,
    isAllAttendees?: boolean,
    isAllPeople?: boolean
  ) => void;
  hideAllAttendees: boolean;
};

const SelectRecipientsSuggestedCategories = (
  props: SelectRecipientsSuggestedCategoriesProps
) => {
  const {
    event,
    selectedCategories,
    excludingMode,
    selectOnPress,
    hideAllAttendees,
  } = props;
  const [showTipsModal, setShowTipsModal] = useState(false);

  const iconColor = useMemo(
    () => (excludingMode ? Colors.RED3 : Colors.PURPLE7),
    [excludingMode]
  );

  const isLinkedEvent = useMemo(
    () => isLinkedEventType(event.eventType),
    [event.eventType]
  );

  const isExternalLink = useMemo(
    () => isEventExternalLink(event.eventType) && !isLinkedEvent,
    [event.eventType, isLinkedEvent]
  );

  const isEventRelated = useMemo(
    () => isEventRelatedType(event.eventType),
    [event.eventType]
  );

  const isProductRelated = useMemo(
    () => isProductWithCheckout(event.eventType, event.accessLevel),
    [event.eventType, event.accessLevel]
  );

  const isProductSpecific = useMemo(
    () => isProductSpecificType(event.eventType),
    [event.eventType]
  );

  const sections = useMemo(() => {
    const nextSections: SuggestedCategorySection[] = [];
    const rightChevron = (
      <Icon
        icon="ion:chevron-right"
        height={16}
        style={{ color: Colors.GRAY1 }}
      />
    );

    if (!excludingMode && !hideAllAttendees) {
      nextSections.push({
        key: "all-people-or-attendees",
        title: isEventRelated ? "All Attendees" : "All People",
        icon: (
          <Icon icon="ion:people" height={32} style={{ color: iconColor }} />
        ),
        onPress: () =>
          isEventRelated
            ? selectOnPress(0, true)
            : selectOnPress(0, false, true),
        isCheckSelected: selectedCategories.some((category) =>
          isEventRelated
            ? category.type === SubSelectionType.STATUS &&
              category.id === SubSelectionStatusId.ATTENDEES
            : category.type === SubSelectionType.ALL_PEOPLE
        ),
      });
    }

    if (isEventRelated && !isLinkedEvent) {
      nextSections.push({
        key: "status",
        title: "Status",
        icon: <Icon icon="ion:scan" height={30} style={{ color: iconColor }} />,
        onPress: () => selectOnPress(1),
        alternateRightIcon: rightChevron,
      });
    }

    nextSections.push({
      key: "form-responses",
      title: "Form Responses",
      icon: <Icon icon="ion:document" height={30} color={iconColor} />,
      onPress: () => selectOnPress(isExternalLink || isLinkedEvent ? 1 : 2),
      alternateRightIcon: rightChevron,
    });

    if (isEventRelated || isProductRelated) {
      nextSections.push({
        key: "ticket-type",
        title: isProductSpecific ? "Order Options" : "Ticket Type",
        icon: (
          <Icon icon="ion:ticket" height={30} style={{ color: iconColor }} />
        ),
        onPress: () =>
          selectOnPress(isLinkedEvent || isProductSpecific ? 2 : 3),
        alternateRightIcon: rightChevron,
      });
    }

    if (
      isProductWithCheckout(event.eventType, event.accessLevel) &&
      isEventTicketsPaid(event)
    ) {
      nextSections.push({
        key: "refunded",
        title: "Refunded",
        icon: (
          <Icon icon="ion:receipt" height={30} style={{ color: iconColor }} />
        ),
        onPress: () => selectOnPress(isEventRelated ? 4 : 3),
        alternateRightIcon: rightChevron,
      });
    }

    return nextSections;
  }, [
    excludingMode,
    hideAllAttendees,
    isEventRelated,
    isLinkedEvent,
    iconColor,
    isProductRelated,
    event,
    selectedCategories,
    selectOnPress,
    isExternalLink,
    isProductSpecific,
  ]);

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 7 }}>
        <div className="AlignedRow" style={{ gap: 7 }}>
          <span className="bodySubtext">Browse Categories</span>
          <Icon
            icon="mdi:information-outline"
            onClick={() => setShowTipsModal(true)}
            height={14}
            style={{ marginBottom: -1, cursor: "pointer", color: Colors.GRAY1 }}
          />
        </div>
        {sections.map((section) => (
          <Fragment key={section.key}>
            <MassTextsActionItem
              title={section.title}
              icon={section.icon}
              onPress={section.onPress}
              alternateRightIcon={section.alternateRightIcon}
              checkColor={iconColor}
              isCheckSelected={section.isCheckSelected}
            />
            <ActionItemHorizontalDivider />
          </Fragment>
        ))}
      </div>
      {showTipsModal ? (
        <TextCategoriesTipsModal setModalVisible={setShowTipsModal} />
      ) : null}
    </>
  );
};

export default SelectRecipientsSuggestedCategories;
